import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Send,
  CalendarCheck,
  MessageCircle,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useApi } from '../api/client';
import { useQuickLog } from '../components/QuickLogProvider';
import type {
  WeeklyMetricKey,
  WeeklyMetrics,
  WeeklyResponse,
} from '../types/api';

// Pipeline-stage colors must match LegacyChats.tsx so the funnel feels
// like the same data Donna sees on the tracker grid.
const STATUS_TONE: Record<string, string> = {
  sent: 'bg-brand-mint text-brand-forest',
  replied: 'bg-brand-sky text-brand-forest',
  interested: 'bg-brand-lime text-brand-forest',
  booked: 'bg-brand-lime-700 text-white',
  discovery_call: 'bg-brand-violet text-white',
  // The Client bar is keyed on client_hold (it counts client_hold or beyond).
  client_hold: 'bg-brand-sun text-brand-forest',
};

interface MetricSpec {
  key: WeeklyMetricKey;
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  /** Tailwind bg-* class for the per-day mini bar / stat tile. */
  barClass: string;
  /** Tailwind text-* class for the count number. */
  countClass: string;
}

// The 7-day grid and the week-over-week comparison render this same list, so
// the comparison totals are always the sum of the grid's days. "Sent" is the
// `outreaches` metric relabelled to match the pipeline vocabulary.
const METRICS: MetricSpec[] = [
  {
    key: 'outreaches',
    label: 'Sent',
    shortLabel: 'Sent',
    icon: Send,
    barClass: 'bg-brand-lime',
    countClass: 'text-brand-forest',
  },
  {
    key: 'legacy_chats_scheduled',
    label: 'Legacy Chats Scheduled',
    shortLabel: 'Scheduled',
    icon: CalendarCheck,
    barClass: 'bg-brand-mint',
    countClass: 'text-brand-forest',
  },
  {
    key: 'legacy_chats_completed',
    label: 'Legacy Chats Completed',
    shortLabel: 'Completed',
    icon: MessageCircle,
    barClass: 'bg-brand-sky-600',
    countClass: 'text-brand-forest',
  },
  {
    key: 'discovery_calls',
    label: 'Discovery Calls',
    shortLabel: 'Discovery',
    icon: Sparkles,
    barClass: 'bg-brand-violet',
    countClass: 'text-brand-violet-600',
  },
];

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Format a YYYY-MM-DD as `Mon May 4` (local time, suitable for headers). */
function formatDayLabel(iso: string): string {
  // Construct in local time using ISO parts so we don't shift across the
  // UTC-vs-local boundary at the top of a date.
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function formatRangeLabel(weekStart: string): string {
  const [y, m, d] = weekStart.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  const end = new Date(y, m - 1, d + 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const startStr = start.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const endStr = end.toLocaleDateString(undefined, {
    month: sameMonth ? undefined : 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${startStr} – ${endStr}`;
}

function shiftWeek(weekStart: string, deltaWeeks: number): string {
  const [y, m, d] = weekStart.split('-').map(Number);
  const dt = new Date(y, m - 1, d + deltaWeeks * 7);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function currentMondayIso(): string {
  const now = new Date();
  // JS getDay: Sunday=0..Saturday=6. Convert to "days since Monday".
  const dow = now.getDay();
  const sinceMonday = (dow + 6) % 7;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - sinceMonday);
  const yyyy = monday.getFullYear();
  const mm = String(monday.getMonth() + 1).padStart(2, '0');
  const dd = String(monday.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function Weekly() {
  const apiFetch = useApi();
  const [weekStart, setWeekStart] = useState<string>(currentMondayIso());
  const [data, setData] = useState<WeeklyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWeek = useCallback(
    async (ws: string) => {
      setLoading(true);
      try {
        const r = await apiFetch(`/weekly?week_start=${ws}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d: WeeklyResponse = await r.json();
        setData(d);
        setError(null);
      } catch (e) {
        setError(String(e));
      } finally {
        setLoading(false);
      }
    },
    [apiFetch],
  );

  useEffect(() => {
    fetchWeek(weekStart);
  }, [fetchWeek, weekStart]);

  // Refetch when a quick-log fires — counts may have moved.
  const { onLogged } = useQuickLog();
  useEffect(() => {
    return onLogged(() => {
      fetchWeek(weekStart);
    });
  }, [onLogged, fetchWeek, weekStart]);

  const isCurrentWeek = weekStart === currentMondayIso();
  const nextDisabled = isCurrentWeek;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center gap-3">
        <BarChart3 className="h-6 w-6 text-brand-mint-600" />
        <h1 className="font-display text-3xl font-black text-brand-forest">
          Weekly
        </h1>
        <p className="text-sm text-brand-sage">
          {data ? formatRangeLabel(data.week_start) : '—'}
        </p>
      </div>

      <WeekNavigator
        weekStart={weekStart}
        onPrev={() => setWeekStart((ws) => shiftWeek(ws, -1))}
        onNext={() => setWeekStart((ws) => shiftWeek(ws, 1))}
        onThisWeek={() => setWeekStart(currentMondayIso())}
        nextDisabled={nextDisabled}
        isCurrent={isCurrentWeek}
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {data == null ? (
        <div className="flex items-center gap-2 text-brand-sage">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : (
        <div className="space-y-10">
          <DayGrid data={data} loading={loading} />
          <FunnelSection funnel={data.funnel} />
          <WeekOverWeek
            weekTotals={data.week_totals}
            priorTotals={data.prior_totals}
            targets={data.targets}
          />
        </div>
      )}
    </div>
  );
}

function WeekNavigator({
  weekStart,
  onPrev,
  onNext,
  onThisWeek,
  nextDisabled,
  isCurrent,
}: {
  weekStart: string;
  onPrev: () => void;
  onNext: () => void;
  onThisWeek: () => void;
  nextDisabled: boolean;
  isCurrent: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onPrev}
        className="flex items-center gap-1 rounded-md border border-brand-sage/40 bg-white px-3 py-1.5 text-sm font-bold text-brand-forest transition hover:border-brand-lime hover:text-brand-lime-700"
      >
        <ChevronLeft className="h-4 w-4" />
        Prev
      </button>
      <button
        type="button"
        onClick={onThisWeek}
        disabled={isCurrent}
        className={[
          'rounded-md border px-3 py-1.5 text-sm font-bold transition',
          isCurrent
            ? 'border-brand-lime bg-brand-lime/15 text-brand-forest'
            : 'border-brand-sage/40 bg-white text-brand-forest hover:border-brand-lime hover:text-brand-lime-700',
        ].join(' ')}
      >
        This week
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled}
        className={[
          'flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-bold transition',
          nextDisabled
            ? 'cursor-not-allowed border-brand-sage/20 bg-brand-paper text-brand-sage/50'
            : 'border-brand-sage/40 bg-white text-brand-forest hover:border-brand-lime hover:text-brand-lime-700',
        ].join(' ')}
      >
        Next
        <ChevronRight className="h-4 w-4" />
      </button>
      <span className="ml-2 hidden text-xs text-brand-sage sm:inline">
        Week of {formatDayLabel(weekStart)}
      </span>
    </div>
  );
}

function DayGrid({ data, loading }: { data: WeeklyResponse; loading: boolean }) {
  return (
    <section>
      <SectionTitle icon={Calendar} label="7-day grid" />
      <div className="relative">
        {loading && (
          <div className="absolute right-0 top-0 z-10 flex items-center gap-1 text-xs text-brand-sage">
            <Loader2 className="h-3 w-3 animate-spin" />
            Refreshing…
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {data.days.map((day) => (
            <DayCard
              key={day.date}
              day={day}
              dayMetrics={METRICS}
              targets={data.targets}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function DayCard({
  day,
  dayMetrics,
  targets,
}: {
  day: WeeklyResponse['days'][number];
  dayMetrics: MetricSpec[];
  targets: WeeklyResponse['targets'];
}) {
  return (
    <motion.div
      layout
      whileHover={{ y: -2 }}
      className={[
        'rounded-2xl border bg-white p-3 shadow-sm transition',
        day.is_today
          ? 'border-brand-lime ring-2 ring-brand-lime/40 shadow-md'
          : 'border-brand-sage/20',
        day.is_weekend && !day.is_today ? 'opacity-60' : '',
      ].join(' ')}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-xs font-black uppercase tracking-wider text-brand-sage">
            {WEEKDAY_LABELS[day.weekday]}
          </div>
          <div className="font-display text-lg font-black text-brand-forest">
            {formatDayLabel(day.date)}
          </div>
        </div>
        {day.complete && (
          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 18 }}
            className="grid h-7 w-7 place-items-center rounded-full bg-brand-lime text-brand-forest shadow"
            title="All daily targets met"
          >
            <Check className="h-4 w-4" strokeWidth={3.5} />
          </motion.div>
        )}
      </div>

      <div className="mt-3 space-y-2">
        {dayMetrics.map((m) => {
          const count = day.metrics[m.key];
          const goal = targets[m.key]?.daily ?? 0;
          const pct = goal > 0 ? Math.min(100, (count / goal) * 100) : 0;
          const reached = goal > 0 && count >= goal;
          return (
            <div key={m.key}>
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-bold uppercase tracking-wider text-brand-sage">
                  {m.shortLabel}
                </span>
                <span
                  className={[
                    'font-display font-black',
                    reached ? 'text-brand-lime-700' : 'text-brand-forest',
                  ].join(' ')}
                >
                  {count}
                  {goal > 0 && (
                    <span className="ml-1 text-[10px] font-bold text-brand-sage">
                      /{goal}
                    </span>
                  )}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-brand-sage/15">
                <div
                  className={[
                    'h-full rounded-full transition-[width] duration-500',
                    reached ? 'bg-brand-lime' : m.barClass,
                  ].join(' ')}
                  style={{ width: `${goal > 0 ? pct : count > 0 ? 100 : 0}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

function FunnelSection({ funnel }: { funnel: WeeklyResponse['funnel'] }) {
  const max = useMemo(
    () => funnel.reduce((acc, s) => Math.max(acc, s.count), 0),
    [funnel],
  );
  // Conversion ratios: stage_n / stage_{n-1}.
  const rows = useMemo(
    () =>
      funnel.map((stage, i) => {
        const prev = i > 0 ? funnel[i - 1] : null;
        const ratio = prev && prev.count > 0 ? stage.count / prev.count : null;
        return { stage, ratio };
      }),
    [funnel],
  );

  return (
    <section>
      <SectionTitle icon={BarChart3} label="Pipeline funnel (all-time)" />
      {max === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-sage/40 bg-white p-6 text-center text-sm text-brand-sage">
          No status events yet. Log an outreach to start filling the funnel.
        </div>
      ) : (
        <div className="space-y-2 rounded-2xl border border-brand-sage/30 bg-white p-4 shadow-sm">
          {rows.map(({ stage, ratio }, i) => {
            const widthPct = (stage.count / max) * 100;
            return (
              <div key={stage.short_name}>
                {i > 0 && (
                  <div className="mb-1 ml-2 flex items-center gap-1 text-[11px] font-bold text-brand-sage">
                    <span className="text-brand-sage/60">↓</span>
                    {ratio == null ? (
                      <span>—</span>
                    ) : (
                      <span>{Math.round(ratio * 100)}% conversion</span>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-32 shrink-0 text-right text-xs font-bold uppercase tracking-wider text-brand-forest">
                    {stage.label}
                  </div>
                  <div className="relative h-9 flex-1 overflow-hidden rounded-md bg-brand-sage/10">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.max(widthPct, stage.count > 0 ? 4 : 0)}%` }}
                      transition={{ type: 'spring', stiffness: 90, damping: 20 }}
                      className={[
                        'flex h-full items-center justify-end pr-3',
                        STATUS_TONE[stage.short_name] ?? 'bg-brand-sage/30 text-brand-forest',
                      ].join(' ')}
                    >
                      <span className="font-display text-sm font-black">
                        {stage.count}
                      </span>
                    </motion.div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function WeekOverWeek({
  weekTotals,
  priorTotals,
  targets,
}: {
  weekTotals: WeeklyMetrics;
  priorTotals: WeeklyMetrics;
  targets: WeeklyResponse['targets'];
}) {
  return (
    <section>
      <SectionTitle icon={Calendar} label="This week vs last week" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {METRICS.map((m) => (
          <DeltaTile
            key={m.key}
            spec={m}
            current={weekTotals[m.key]}
            prior={priorTotals[m.key]}
            weeklyGoal={targets[m.key]?.weekly ?? 0}
          />
        ))}
      </div>
    </section>
  );
}

function DeltaTile({
  spec,
  current,
  prior,
  weeklyGoal,
}: {
  spec: MetricSpec;
  current: number;
  prior: number;
  weeklyGoal: number;
}) {
  const delta = current - prior;
  const pctChange = prior > 0 ? (delta / prior) * 100 : current > 0 ? 100 : 0;
  const Direction = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  const directionClass =
    delta > 0
      ? 'text-brand-lime-700 bg-brand-lime/15'
      : delta < 0
        ? 'text-brand-coral-600 bg-brand-coral/15'
        : 'text-brand-sage bg-brand-sage/10';
  const Icon = spec.icon;
  const goalReached = weeklyGoal > 0 && current >= weeklyGoal;

  return (
    <div
      className={[
        'rounded-2xl border bg-white p-4 shadow-sm',
        goalReached
          ? 'border-brand-lime ring-2 ring-brand-lime/30'
          : 'border-brand-sage/20',
      ].join(' ')}
    >
      <div className="flex items-center gap-2">
        <div
          className={[
            'grid h-8 w-8 place-items-center rounded-lg',
            spec.barClass,
            'text-brand-forest',
          ].join(' ')}
        >
          <Icon className="h-4 w-4" strokeWidth={2.5} />
        </div>
        <div className="text-xs font-bold uppercase tracking-wider text-brand-sage">
          {spec.label}
        </div>
      </div>

      <div className="mt-3 flex items-baseline gap-2">
        <span
          className={[
            'font-display text-3xl font-black leading-none',
            goalReached ? 'text-brand-lime-700' : spec.countClass,
          ].join(' ')}
        >
          {current}
        </span>
        {weeklyGoal > 0 && (
          <span className="text-xs font-bold text-brand-sage">
            / {weeklyGoal}
          </span>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-brand-sage">Last week: {prior}</span>
        <span
          className={[
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-bold',
            directionClass,
          ].join(' ')}
        >
          <Direction className="h-3 w-3" />
          {delta === 0
            ? 'Same'
            : prior === 0
              ? `+${delta} (new)`
              : `${delta > 0 ? '+' : ''}${delta} (${Math.round(pctChange)}%)`}
        </span>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-brand-sage" />
      <h2 className="text-xs font-bold uppercase tracking-wider text-brand-sage">
        {label}
      </h2>
    </div>
  );
}